import {
    CirclePartner,
    CircleValidationFieldName,
    CircleValidationFieldWithValue,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import { ERROR_CODE } from '@rhg/constants';
import { identifyApiErrorCode } from '../axios';
import { InvitationErrorMessage } from '../invitations';

export const SPONSOR_CODE_FIELD_NAME =
    'sponsor_code' as CircleValidationFieldName;

/**
 * Returns `true` if the partner's validation fields consist **only** of the
 * sponsor code field. Used to determine whether the magic link auto-submit flow
 * can bypass manual eligibility entry (i.e., no other fields need to be filled).
 *
 * @param partner - The partner to check
 * @returns `true` when the partner has a non-empty validations array where every field is `SPONSOR_CODE_FIELD_NAME`
 */
export function hasSponsorCodeOnlyValidations(
    partner: CirclePartner | null | undefined
): boolean {
    const validations = partner?.sources?.fieldValidation?.validations;
    if (!validations?.length) return false;
    return validations.every((field) => field.name === SPONSOR_CODE_FIELD_NAME);
}

/**
 * Constructs the `eligibilityFormValues` array for passing a sponsor code to `submitFlow`.
 *
 * @param sponsorCode - The sponsor code value
 * @returns Array with a single entry for the `SPONSOR_CODE_FIELD_NAME` field
 */
export function buildSponsorCodeEligibilityFormValues(
    sponsorCode: string
): CircleValidationFieldWithValue[] {
    return [{ name: SPONSOR_CODE_FIELD_NAME, value: sponsorCode }];
}

export enum EligibilityErrorType {
    InvalidEmailDomain = 'invalidEmailDomain',
    EligibilityDenied = 'eligibilityDenied',
    MemberInactive = 'memberInactive',
    MemberActiveInFuture = 'memberActiveInFuture',
    ContractCoveredLivesExceeded = 'contractCoveredLivesExceeded',
    MemberEligibilityUsed = 'memberEligibilityUsed',
    ServerError = 'serverError',
}

const UPGRADE_COUNTRY_ERROR_CODES: string[] = [
    ERROR_CODE.UPGRADE_INTERNATIONAL_USER_NOT_ALLOWED_FOR_DOMESTIC_PARTNER,
    ERROR_CODE.UPGRADE_UNEXPECTED_INTERNATIONAL_USER,
];

/**
 * Returns `true` when an error is the upgrade transition matrix rejecting an
 * international user against a domestic partner. That is the same country
 * mismatch as `sender_recipient_country_mismatch`, so it resolves to the
 * country dialog rather than to an eligibility error.
 *
 * @param e - The error to inspect
 * @returns `true` for an upgrade country rejection at status `403`
 */
export function isUpgradeCountryError(e: CustomAxiosError | null): boolean {
    if (e?.response?.status !== 403) {
        return false;
    }
    const errorCode = identifyApiErrorCode(e);
    return !!errorCode && UPGRADE_COUNTRY_ERROR_CODES.includes(errorCode);
}

/**
 * Only identifies eligibility errors.
 * Other error types like 500 server errors, or 400 request errors
 * should be handled by the caller
 */
export function identifyEligibilityErrorType(
    e: CustomAxiosError | null
): EligibilityErrorType | undefined {
    if (e?.response?.status === 403) {
        const errorCode = e ? identifyApiErrorCode(e) : null;

        // Need to early return if sponsorship code caused a country mismatch error
        // so that it doesn't get misidentified as an eligibility error
        // The error will be correctly handled by the InvitationErrorDialog
        if (
            errorCode === InvitationErrorMessage.country_mismatch ||
            isUpgradeCountryError(e)
        ) {
            return;
        }

        if (errorCode) {
            switch (errorCode as ERROR_CODE) {
                case ERROR_CODE.EMAIL_DOMAIN_NOT_FOUND:
                    return EligibilityErrorType.InvalidEmailDomain;
                case ERROR_CODE.MEMBER_INACTIVE:
                    return EligibilityErrorType.MemberInactive;
                case ERROR_CODE.MEMBER_ACTIVE_IN_FUTURE:
                    return EligibilityErrorType.MemberActiveInFuture;
                case ERROR_CODE.CONTRACT_COVERED_LIVES_EXCEEDED:
                    return EligibilityErrorType.ContractCoveredLivesExceeded;
                case ERROR_CODE.MEMBER_ELIGIBILITY_USED:
                    return EligibilityErrorType.MemberEligibilityUsed;
            }
        }
        return EligibilityErrorType.EligibilityDenied;
    }
}
